import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tags, Check, GitMerge, Loader2, Trash2, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ColorSwatches } from "@/lib/categoryColors";

const slugOf = (s) => s.replace(/\W+/g, "-");

function Row({ c, others, onPick, busy, color, onColor }) {
  const [name, setName] = useState(c.name);
  const [into, setInto] = useState("");
  useEffect(() => { setName(c.name); setInto(""); }, [c.name]);
  const slug = slugOf(c.name);
  const clash = others.some((o) => o.name === name.trim());
  return (
    <div data-testid={`cat-row-${slug}`} className="rounded-xl border border-zinc-800 bg-zinc-900 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input data-testid={`cat-name-${slug}`} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} className="h-9 min-w-0 flex-1 basis-40 border-zinc-800 bg-zinc-950" />
        <span className="shrink-0 rounded-full bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400" data-testid={`cat-count-${slug}`}>{c.count} article{c.count > 1 ? "s" : ""}</span>
        <Button data-testid={`cat-rename-${slug}`} size="sm" disabled={busy || !name.trim() || name.trim() === c.name} onClick={() => onPick({ kind: clash ? "merge" : "rename", sources: [c.name], target: name.trim() })}
          className="h-9 shrink-0 rounded-lg bg-[#FF6A00] px-3 text-white hover:bg-[#E65F00]" title={clash ? "Ce nom existe : les catégories seront fusionnées" : "Renommer"}>
          {clash ? <GitMerge className="h-4 w-4" /> : <Check className="h-4 w-4" />}
        </Button>
        <Button data-testid={`cat-delete-${slug}`} size="sm" variant="outline" disabled={busy} onClick={() => onPick({ kind: "delete", sources: [c.name], target: "" })} title="Supprimer"
          className="h-9 shrink-0 rounded-lg border-zinc-800 bg-transparent px-3 text-zinc-500 hover:border-red-500/40 hover:bg-transparent hover:text-red-400"><Trash2 className="h-4 w-4" /></Button>
      </div>
      {onColor && <div className="mt-2"><ColorSwatches testid={`cat-color-${slug}`} value={color} onChange={onColor} /></div>}
      {others.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
          <GitMerge className="h-3.5 w-3.5" />Fusionner dans
          <select data-testid={`cat-merge-select-${slug}`} value={into} onChange={(e) => setInto(e.target.value)} className="min-w-0 flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1 text-zinc-200">
            <option value="">Choisir une catégorie…</option>
            {others.map((o) => <option key={o.name} value={o.name}>{o.name} ({o.count})</option>)}
          </select>
          <Button data-testid={`cat-merge-${slug}`} size="sm" variant="outline" disabled={busy || !into} onClick={() => onPick({ kind: "merge", sources: [c.name], target: into })} className="h-7 rounded-md border-zinc-700 bg-transparent px-2 text-xs text-zinc-200 hover:bg-zinc-800">Fusionner</Button>
        </div>
      )}
    </div>
  );
}

function Suggestions({ list, onPick, busy }) {
  if (!list.length) return null;
  return (
    <div data-testid="cat-suggestions" className="space-y-2 rounded-xl border border-[#FFD500]/30 bg-[#FFD500]/5 p-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-[#FFD500]"><Sparkles className="h-3.5 w-3.5" />Doublons probables</p>
      {list.map((s, i) => (
        <div key={s.names.join("|")} data-testid={`cat-suggestion-${i}`} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="min-w-0 flex-1 text-zinc-300">{s.names.map((n) => `« ${n} »`).join(", ")} → <b className="text-white">« {s.target} »</b></span>
          <Button data-testid={`cat-suggestion-apply-${i}`} size="sm" disabled={busy} onClick={() => onPick({ kind: "merge", sources: s.names.filter((n) => n !== s.target), target: s.target })}
            className="h-7 rounded-full bg-[#FF6A00] px-3 text-xs text-white hover:bg-[#E65F00]"><GitMerge className="mr-1 h-3.5 w-3.5" />Fusionner</Button>
        </div>
      ))}
    </div>
  );
}

function Confirm({ pending, setPending, cats, busy, onOk }) {
  const { kind, sources, target } = pending;
  const count = sources.reduce((a, s) => a + ((cats.find((c) => c.name === s) || {}).count || 0), 0);
  const others = cats.filter((c) => !sources.includes(c.name));
  const needTarget = kind === "delete" && count > 0;
  const src = sources.map((s) => `« ${s} »`).join(", ");
  return (
    <div data-testid="cat-confirm" className="space-y-4 rounded-xl border border-[#FF6A00]/40 bg-[#FF6A00]/5 p-4">
      <p className="text-sm text-white">
        {kind === "delete" ? <>Supprimer <b>{src}</b> ?</> : kind === "merge" ? <>Fusionner <b>{src}</b> dans <b>« {target} »</b> ?</> : <>Renommer <b>{src}</b> en <b>« {target} »</b> ?</>}
      </p>
      {needTarget && (
        <label className="block text-xs text-zinc-400">Déplacer ses {count} article(s) dans
          <select data-testid="cat-delete-target" value={target} onChange={(e) => setPending({ ...pending, target: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm text-zinc-200">
            <option value="">Choisir une catégorie…</option>
            {others.map((o) => <option key={o.name} value={o.name}>{o.name} ({o.count})</option>)}
          </select>
        </label>
      )}
      <p className="text-xs text-zinc-400">{kind === "delete" ? (count ? "La catégorie disparaîtra de la liste." : "Aucun article n'utilise cette catégorie.") : `${count} article(s) seront mis à jour.${kind === "merge" ? " Les anciennes catégories disparaîtront de la liste." : ""}`}</p>
      <div className="flex justify-end gap-2">
        <Button data-testid="cat-confirm-cancel" variant="outline" onClick={() => setPending(null)} className="rounded-full border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800">Annuler</Button>
        <Button data-testid="cat-confirm-ok" disabled={busy || (needTarget && !target)} onClick={onOk} className={`rounded-full text-white ${kind === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-[#FF6A00] hover:bg-[#E65F00]"}`}>{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Confirmer</Button>
      </div>
    </div>
  );
}

export default function CategoryManager({ onChanged }) {
  const { org, refreshOrg } = useAuth();
  const setColor = async (name, value) => {
    const colors = { ...(org?.category_colors || {}) };
    if (value) colors[name] = value; else delete colors[name];
    try { const { data } = await api.put("/org", { category_colors: colors }); refreshOrg(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couleur non enregistrée"); }
  };
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState(null);
  const [sugg, setSugg] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const loadSugg = () => api.get("/catalog-categories/suggestions").then(({ data }) => setSugg(data)).catch(() => setSugg([]));
  useEffect(() => {
    if (open) { api.get("/catalog-categories").then(({ data }) => setCats(data)).catch(() => setCats([])); loadSugg(); }
  }, [open]);
  const apply = async () => {
    const { kind, sources, target } = pending;
    setBusy(true);
    try {
      let n = 0;
      for (const s of sources) {
        const { data } = kind === "delete"
          ? await api.post("/catalog-categories/delete", { name: s, target })
          : await api.post("/catalog-categories/rename", { source: s, target });
        n += data.updated ?? data.moved ?? 0;
        setCats(data.categories);
        onChanged?.(s, kind === "delete" ? target : target, kind === "delete");
      }
      const label = kind === "delete" ? "Catégorie supprimée" : kind === "merge" ? `Fusionné dans « ${target} »` : `Renommée en « ${target} »`;
      toast.success(label, { description: `${n} article(s) mis à jour` });
      loadSugg();
      const me = await api.get("/auth/me").catch(() => null);
      if (me?.data?.org) refreshOrg(me.data.org);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); setPending(null); }
  };
  return (
    <>
      <button data-testid="manage-categories-btn" onClick={() => setOpen(true)} title="Gérer les catégories"
        className="flex h-9 w-9 items-center justify-center rounded-full border border-zinc-800 bg-zinc-900 text-zinc-400 transition-colors hover:border-[#FF6A00]/50 hover:text-white"><Tags className="h-4 w-4" /></button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPending(null); }}>
        <DialogContent data-testid="categories-dialog" className="max-h-[85vh] overflow-y-auto border-zinc-800 bg-zinc-950 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Gérer les catégories</DialogTitle>
            <DialogDescription className="text-zinc-500">Renommez, fusionnez ou supprimez une catégorie : tous les articles concernés sont mis à jour.</DialogDescription>
          </DialogHeader>
          {pending ? <Confirm pending={pending} setPending={setPending} cats={cats || []} busy={busy} onOk={apply} />
            : !cats ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-zinc-500" />
            : cats.length === 0 ? <p className="py-6 text-center text-sm text-zinc-500">Aucune catégorie pour le moment.</p>
            : (
              <div className="space-y-2">
                <Suggestions list={sugg} onPick={setPending} busy={busy} />
                {cats.map((c) => <Row key={c.name} c={c} others={cats.filter((o) => o.name !== c.name)} busy={busy} onPick={setPending}
                  color={(org?.category_colors || {})[c.name] || null} onColor={org?.category_colors_enabled ? (v) => setColor(c.name, v) : null} />)}
              </div>
            )}
        </DialogContent>
      </Dialog>
    </>
  );
}
